import React, { useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { fmtEuro0, MONTHS_EL_SHORT } from "@/lib/format";
import { toast } from "@/components/ui/sonner";
import { BarChart3, Copy, ArrowRightLeft } from "lucide-react";

const M = Array.from({ length: 12 }, (_, i) => i + 1);
const zeros = () => Object.fromEntries(M.map((m) => [m, 0]));

function buildStats(events) {
  const s = { count: zeros(), net: zeros(), vat: zeros(), gross: zeros(), paid: zeros(), cats: {} };
  events.forEach((ev) => {
    const m = Number((ev.event_date || "").slice(5, 7));
    if (!m) return;
    s.count[m] += 1;
    s.gross[m] += Number(ev.gross) || 0;
    s.paid[m] += Number(ev.paid) || 0;
    (ev.lines || []).forEach((l) => {
      const c = l.category || "Λοιπά";
      s.cats[c] = s.cats[c] || { net: zeros(), vat: zeros(), gross: zeros() };
      const n = Number(l.net) || 0, v = Number(l.vat) || 0;
      s.cats[c].net[m] += n; s.cats[c].vat[m] += v; s.cats[c].gross[m] += n + v;
      s.net[m] += n;
      s.vat[m] += v;
    });
  });
  return s;
}

const sum = (row) => M.reduce((a, m) => a + row[m], 0);

function Row({ label, row, money = true, strong = false, testid }) {
  return (
    <tr className={`border-t border-[#E3DED4] ${strong ? "bg-[#F7F5F1] font-semibold text-[#45423C]" : ""}`} data-testid={testid}>
      <td className="px-3 py-2 whitespace-nowrap sticky left-0 bg-inherit">{label}</td>
      {M.map((m) => <td key={m} className="px-2 py-2 text-right tabular whitespace-nowrap">{row[m] ? (money ? fmtEuro0(row[m]) : row[m]) : "—"}</td>)}
      <td className="px-3 py-2 text-right tabular font-semibold whitespace-nowrap">{money ? fmtEuro0(sum(row)) : sum(row)}</td>
    </tr>
  );
}

export function EventsMonthly({ events, year }) {
  const s = useMemo(() => buildStats(events), [events]);
  const qc = useQueryClient();
  const push = useMutation({
    mutationFn: async () => (await api.post(`/finance/income-budget/from-events?year=${year}`)).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["income-budget"] });
      toast.success(`Προϋπολογισμός Εσόδων ${d.year}: Εκδηλώσεις = ${fmtEuro0(d.total)} (${d.events} εκδηλώσεις)`);
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  const confirmPush = () => {
    if (window.confirm(`Να γραφτούν τα καθαρά έσοδα εκδηλώσεων ανά μήνα (${fmtEuro0(sum(s.net))}) στη γραμμή «Έσοδα Εκδηλώσεων / Γάμων» του Προϋπολογισμού Εσόδων ${year}; Τα τωρινά ποσά αυτής της γραμμής αντικαθίστανται — οι άλλες γραμμές δεν αλλάζουν.`)) push.mutate();
  };
  const copyNet = async () => {
    try {
      await navigator.clipboard.writeText(M.map((m) => Math.round(s.net[m] * 100) / 100).join("\t"));
      toast.success("Αντιγράφηκαν τα καθαρά έσοδα ανά μήνα (Ιαν–Δεκ) — επικόλληση στον Προϋπολογισμό Εσόδων");
    } catch { toast.error("Δεν ήταν δυνατή η αντιγραφή"); }
  };
  return (
    <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden" data-testid="ev-monthly">
      <div className="px-5 py-4 border-b border-[#E3DED4] flex flex-wrap items-center gap-3">
        <BarChart3 className="h-4 w-4 text-[#C86235]" />
        <h2 className="font-medium text-[#45423C]">Έσοδα εκδηλώσεων ανά μήνα {year}</h2>
        <span className="text-xs text-[#6F6A60]">βάσει ημερομηνίας εκδήλωσης</span>
        <button data-testid="ev-monthly-copy" onClick={copyNet}
          className="ml-auto inline-flex items-center gap-1.5 rounded-xl border border-[#E3DED4] px-3 py-1.5 text-xs font-semibold text-[#45423C] hover:border-[#B9AE9A]">
          <Copy className="h-3.5 w-3.5" /> Αντιγραφή
        </button>
        <button data-testid="ev-monthly-to-budget" onClick={confirmPush} disabled={push.isPending}
          className="inline-flex items-center gap-1.5 rounded-xl bg-[#45423C] text-white px-3 py-1.5 text-xs font-semibold hover:opacity-90 disabled:opacity-50">
          <ArrowRightLeft className="h-3.5 w-3.5" /> {push.isPending ? "Μεταφορά…" : "Μεταφορά στον Προϋπολογισμό Εσόδων"}
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="bg-[#F1EDE6] text-[11px] uppercase tracking-wider text-[#6F6A60]">
            <th className="px-3 py-3 text-left sticky left-0 bg-[#F1EDE6]" />
            {M.map((m) => <th key={m} className="px-2 py-3 text-right">{MONTHS_EL_SHORT[m]}</th>)}
            <th className="px-3 py-3 text-right">Σύνολο</th>
          </tr></thead>
          <tbody className="bg-white">
            <Row label="Εκδηλώσεις" row={s.count} money={false} testid="ev-monthly-count" />
            {Object.entries(s.cats).sort(([a], [b]) => a.localeCompare(b)).map(([c, r]) => (
              <React.Fragment key={c}>
                <Row label={<span className="font-semibold text-[#45423C]">{c} · Καθαρά</span>} row={r.net} testid={`ev-monthly-cat-net-${c}`} />
                <Row label={<span className="pl-3 text-[#6F6A60]">ΦΠΑ</span>} row={r.vat} testid={`ev-monthly-cat-vat-${c}`} />
                <Row label={<span className="pl-3 text-[#6F6A60]">Σύνολο</span>} row={r.gross} testid={`ev-monthly-cat-gross-${c}`} />
              </React.Fragment>
            ))}
            <Row label="Καθαρά έσοδα (όλες)" row={s.net} strong testid="ev-monthly-net" />
            <Row label="ΦΠΑ (όλες)" row={s.vat} strong testid="ev-monthly-vat" />
            <Row label="Σύνολο με ΦΠΑ (όλες)" row={s.gross} strong testid="ev-monthly-gross" />
            <Row label="Εισπράχθηκαν" row={s.paid} testid="ev-monthly-paid" />
          </tbody>
        </table>
      </div>
    </div>
  );
}
